import _ from "lodash";
import RequestModel from "../models/Request";
import Job from "../models/Job";
import Processors from "../models/Processors";
import { Status } from "../models/Status";
import RequestUtils from "./RequestUtils";
import ConfigUtils from "./ConfigUtils";
import { AOC_DELIVERY_REGION_MAP, DELIVERY_REGION_MAP } from "../constants/CreateMediaRequest";

export const getProcessingLabel = (jobType, field) => {
  return JobUtils.getProcessingLabel(jobType, field);
}

export const getValue = (field, value) => {
  return JobUtils.getValue(field, value);
}

export const buildKey = (source, process) => {
  return !_.isEmpty(process) && !_.isNil(_.get(process, "key")) ? _.get(source, "key") + "_" + _.get(process, "key") : _.get(source, "key")
}

const EXCLUSION_PATTERN = /[\?!@#\$%\^&\*\\\|/"';:><,~`\[\]\{\}\(\)]/; //eslint-disable-line

const SOURCE_LOCATIONS_EXCLUDE_EXTENSIONS = ["HDR_MAM", "J2K_MAM"];

export default class JobUtils {
  static getProcessors() {
    return ["EVENT_SCREENERS", "FILE_CREATION", "VIP_PRE_AIR", "WB_SCREENERS"];
  }

  static transformJobId(jobId) {
    return _.replace(jobId, "_v3", "");
  }

  static skipValidationByJobType = (jobType) => {
    return jobType !== 'EVENT_SCREENERS' && jobType !== 'VIP_PRE_AIR' ;
  }

  static statusCheck(iconType, status, userRole, jobType = null) {
    let flag = true;
    if(_.toLower(userRole) === "adminwithreadaccess" || _.toLower(userRole) === "cr_api" || _.toLower(userRole) === "mediarequest"){
      flag = false;
    }else if (status === "COMPLETE" || status === "CANCELLED" || _.toLower(userRole) === "requestor") {
      if (iconType === "editStatus") {
        if (_.toLower(userRole) === "requestor" && !_.includes(["SUBMITTED", "ON_HOLD"], status)) {
          flag = false;
        } else if ((_.toLower(userRole) === "globaladmin" || _.toLower(userRole) === "superadmin" || _.toLower(userRole) === "ops" || _.toLower(userRole) === "requestor") && (jobType === 'CONTENT_REQUEST_API' || jobType === 'MEDIA_REQUEST')) {
          flag = false;
        } else {
          flag = true;
        }
      } else {
        flag = false;
      }
    } else if((_.toLower(userRole) === "globaladmin" || _.toLower(userRole) === "superadmin" || _.toLower(userRole) === "ops" || _.toLower(userRole) === "requestor") && (jobType === 'CONTENT_REQUEST_API' || jobType === 'MEDIA_REQUEST')) {
       flag = false;
    }
    return flag;
  }

  static isEditable(status) {
    let flag = true;
    if (status === "COMPLETE" || status === "CANCELLED") {
      flag = false;
    }
    return flag;
  }

  static getSummaryDetail(sourceLocation) {
    let sourceField = [];
    let metaData = _.get(sourceLocation, "metadata");
    const inputComponents = ConfigUtils.getInputComponents();
    const inputComp = _.get(inputComponents, "inputComponents[0].dataFields[0]");
    _.map(metaData, (meta) => {
      let field = _.get(inputComp, meta);
      sourceField.push({
        label: field.label,
        fields: [
          {
            type: field.type,
            value: meta,
          },
        ],
      });
    });
    return sourceField;
  }

  static getDeliveryOptionLabel(delivery) {
    return _.find(ConfigUtils.getDeliveryMapping(), (del) => _.get(del, "label") === delivery);
  }

  static getProcessingLabel(jobType, field) {
    let processingInput = _.get(jobType, "processingInput");
    let metadata = _.get(processingInput && processingInput.override, "metadata");
    let overrideField = _.get(metadata && metadata[0], field);
    const inputComponents = ConfigUtils.getInputComponents();
    const inputComp = _.get(inputComponents, "inputComponents[0].dataFields[0]");
    return (overrideField && overrideField.label) ? overrideField.label : (_.get(inputComp, field).label || "");
  }

  static getValue = (field, value) => {
    const inputComponents = ConfigUtils.getInputComponents();
    const inputComp = _.get(inputComponents, "inputComponents[0].dataFields[0]");
    let inputField = _.get(inputComp, field);
    let type = inputField.type;
    switch (type) {
      case "boolean":
        return value === true ? "Yes" : "No";
      default:
        return value;
    }
  };

  static isNoID(source) {
    if (_.has(source, 'assetIdType') && _.get(source, 'assetIdType') === 'No ID Available') {
      return true;
    } else {
      return false;
    }
  }

  static getSourceFileName(source, jobType) {
    // Below condition will take care of the VIP NOID and FILE Creation.
    if(jobType === "WB_SCREENERS") {
      return _.get(source, "assetName") + '_' + _.get(source, "brainiacTitleID");
    }else if ((jobType === "VIP_PRE_AIR" &&  JobUtils.isNoID(source)) || jobType === "FILE_CREATION") {
      return _.get(
        source,
        "assetName",
        _.get(source, "assetFilename")
      );
    } else {
      // Below condition will take care of all Upload Only, Event Screeners and Screeners.com(Without NO ID)
      return _.get(
        source,
        "readOnlyMetadata.assetTitle",
        _.get(source, "assetFilename", _.get(source, "assetName"))
      );
    }
  }

  static checkInvalidFileName(jobType, source, stitchAssetName, isStitch) {
    let _jobType = JobUtils.extractJobTypeProperty(jobType, "jobType");
    let filename = isStitch ? stitchAssetName : JobUtils.getSourceFileName(source, _jobType);
    return filename && _jobType !== 'EVENT_SCREENERS' ? (filename.match(EXCLUSION_PATTERN) || /(?:^\s+)|(?:\s+$)/g.test(filename)) : false;     
  }

  static getDefaultFileName(jobType, sourceLocation, source, keyToFilename, process = {}, stitchAssetName = '', isStitch = false) {
    let _jobType = JobUtils.extractJobTypeProperty(jobType, "jobType");
    let _sourceLocation = JobUtils.extractSourceLocationProperty(sourceLocation, "type");
    let sourceFileName = isStitch ? stitchAssetName : JobUtils.getSourceFileName(source, _jobType);
    let fileName = _.isEmpty(keyToFilename) ? sourceFileName : keyToFilename;
    const isCustomSpec = _.get(process, "fileType") === "Custom";
    //To handle extenstion for upload only => HDR MAM, TECHWOOD & J2K_MAM(No extension carry forward from source location)
    // For these two sources we do not need to carry over the extensions so removing them.
    if (_jobType === "UPLOAD_ONLY" || _jobType === "FILE_CREATION") {
      // This will remove any special characters. Reference from Morph 2
      //fileName = fileName && fileName.replace(/[^a-zA-Z0-9-_. ]/g, "");
      if ((_jobType === "FILE_CREATION" && !isCustomSpec) ||
          (_jobType === "UPLOAD_ONLY" && SOURCE_LOCATIONS_EXCLUDE_EXTENSIONS.includes(_sourceLocation))) {
        fileName = fileName && fileName.replace(/\..*/g, "");
      }
    } else if (_jobType === "VIP_PRE_AIR") {
      fileName = fileName && fileName.replace(/\..*/g, "");
    } else if (_jobType === "WB_SCREENERS") {
      fileName = fileName && fileName.replace(/\..*/g, "");
    }
    if (_.has(process, "fileType")) {
      if (!isCustomSpec) {
        let getFileType = RequestUtils.getExtension(_sourceLocation, process);
        let ext = _.has(getFileType, "ext") ? _.get(getFileType, "ext") : "";
        let abbr = _.get(getFileType, "abbr");
        if (_.isEmpty(keyToFilename)) {
          let addAbbr = (!_.isEmpty(abbr) && (_jobType !== "VIP_PRE_AIR" && _jobType !== "WB_SCREENERS")) ? fileName + "_" + abbr : fileName;
          fileName = _.isEmpty(ext) ? addAbbr : addAbbr + "." + ext;
        } else {
            if (!_.isEmpty(ext)) {
              const baseName = fileName.replace(/\.[^./\\]+$/, "");
              fileName = baseName + "." + ext;
            }
        }
      }

    }
    //MM-382 => All filenames should replace spaces with underscore.
    if (_jobType !== "VIP_PRE_AIR") {
      fileName =  fileName && fileName.trim().replace(/ /g, "_");
    }
    return fileName;
  }

  static getSourceAssetLocation(jobType, sourceType) {
    let jobValue = _.find(ConfigUtils.getSourceMapping(), (job) => _.get(job, "jobType") === jobType);
    let sourceDefinitions = _.get(jobValue, "sourceDefinition");
    return _.find(sourceDefinitions, (source) => {
      return JobUtils.getSourceType(source) === sourceType;
    });
  }

  static needsQC(jobType, sourceType, delivery, requestor) {
    return !JobUtils.isJobAutomated(jobType, sourceType, requestor, delivery) && /^(EVENT_SCREENERS|AVID_INGEST|FILE_CREATION)$/.test(jobType);
  }

  static isQCSupported(job) {
    var jobType = _.get(job, "jobType");
    var sourceType = _.get(job, "sourceType", JobUtils.getSourceType(_.get(job, "source")));
    var delivery = _.get(job, "delivery");
    var requestor = _.get(job, "requestor");

    return (
        !JobUtils.isJobAutomated(jobType, sourceType, requestor, delivery) &&
        /^(EVENT_SCREENERS|AVID_INGEST|FILE_CREATION)$/.test(_.get(job, "jobType"))
    );
  }

  static isSourceOverrideSupported(job) {
    var jobType = _.get(job, "jobType");
    return /^(UPLOAD_ONLY|FILE_CREATION|VIP_PRE_AIR|WB_SCREENERS)$/.test(jobType);
  }

  static isJobAutomated(jobType, sourceLocation, requestor, delivery) {
    // Returning true when either jobType or sourceLocation is empty to avoid the edge case
    // where data entry is enabled when nothing is selected and then becomes disabled based
    // on user selections
    if (_.isEmpty(jobType) || _.isEmpty(sourceLocation)) {
      return true;
    }

    if (sourceLocation === "MORPH_ENCODING" || (jobType === "VIP_PRE_AIR" && sourceLocation === "VIP_HOPPER")) {
      return true;
    }

    // STU-1495 - Initiate automation for certain jobs created by certain users from certain departments

    // Since requestor and delivery were added late this line makes this backwards compatible
    if (_.isUndefined(requestor) || _.isUndefined(delivery)) {
      return false;
    }

    var isCorrectAccount = _.size(delivery) > 0;

    if (jobType === "UPLOAD_ONLY" && sourceLocation === "J2K_MAM" && isCorrectAccount) {
      return true;
    }

    return false;
  }

  static extractJobTypeProperty(jobTypeObject, propertyName) {
    if (_.get(jobTypeObject, "active", true)) {
       return _.get(jobTypeObject, propertyName, "");
    }
    return "";
  }

  static extractSourceLocationProperty(sourceLocationObject, propertyName) {
    return _.get(sourceLocationObject, propertyName, "");
  }

  static extractJobTypeObjectByValue(jobTypeValue) {
    return _.find(ConfigUtils.getJobTypes(), (job) => _.get(job, "jobType") === jobTypeValue);
  }

  static getJobSpecificDeliveryTypes(jobType) {
    let deliveryTypes = [];
     _.get(JobUtils.extractJobTypeObjectByValue(jobType), "deliveryMethods").forEach((ref) => {
      deliveryTypes.push(_.find(ConfigUtils.getDeliveryTypes(), (type) => _.get(type, "ref") === ref));
    });
    return deliveryTypes;
  }

  static extractSourceTypeObjectByValue(jobTypeValue, sourceType) {
    let sourceDefinition = _.get(JobUtils.extractJobTypeObjectByValue(jobTypeValue), "sourceDefinition");
    return _.find(sourceDefinition, (source) => _.get(source, "type") === sourceType && _.get(source, "active") === true);
  }

  //Match source & delivery with new domain model
  static getSourceFields(source, sourceType, jobType, contentProtection, stitchAssetName = '', isStitch = false, sources = [], sourceLocationObject=null) {
    let field = {};
    let sourceLocationObj = JobUtils.getSourceAssetLocation(jobType, sourceType);
    let sourceLocation = Object.assign({}, sourceLocationObj["sourceLocation"]);
    const newSourceLocation =  _.get(sourceLocationObject, "newLocationSubType");
    sourceLocation = {...sourceLocation, newLocationSubType: newSourceLocation}

    if (isStitch) {
      field["type"] = "CompositeJobAsset";
      field["compositeAssetName"] = stitchAssetName;
      field["simpleJobAssets"] = _.map(sources, src => {
        // Recursively call getSourceFields to build simpleJobAssets list
        return _.omit(JobUtils.getSourceFields(src, sourceType, jobType, contentProtection),  "key");
      });
      field["sourceLocation"] = sourceLocation;
      return field;
    }

    _.map(source, (asset, index) => {
      if (_.has(sourceLocation, index, false)) {
        sourceLocation[index] = asset;
      } else {
        field[index] = asset;
      }
    });
    field["sourceLocation"] = sourceLocation;
    field["type"] = _.get(sourceLocationObj, "type");
    //Adding content protection if assetIdType is 'noId'
    if(_.has(source, 'assetIdType') && _.get(source, 'assetIdType') === 'No ID Available'){
      field["contentProtection"] = contentProtection[source.key];
    }
    return field;
  }

  // I need to take a look if we even need it.
  static updateSourceField(source) {
    let sourceObj = Object.assign({}, _.omit(source, "sourceLocation"));
    sourceObj = Object.assign(sourceObj, _.omit(_.get(source, "sourceLocation"), ["locationType", "locationSubType", "newLocationSubType"]));
    return sourceObj;
  }

  static getProcessorField(process, processObj) {
    let screenObj = {};
    let processValue = process;
    // TO DO - Must be some better way to do it. WE should revisit and think.
    // We should have some internal mapping instead of doing it like this.
    // I will work on it later.
    if (_.has(processValue, "screenerEncryptionType")) {
      _.map(processValue, (value, key) => {
        if (key.match(/(address|name|KdmStartDate|KdmEndDate|deliveryEmail|screenerEncryptionType|KdmStartTime|KdmEndTime)/i)) {
          screenObj[key] = value;
          process = _.omit(process, key);
        }
      });
    }
    if (!_.isEmpty(screenObj)) {
      process["screenerEncryption"] = screenObj;
    }
    if (_.has(process, "screenerType", false)) {
      _.set(processObj, "type", (_.camelCase(_.get(process, "screenerType")) === 'other') ? "passThroughProcessor" : _.camelCase(_.get(process, "screenerType")) + "Processor");
    } else {
      _.set(processObj, "type", "transcodingProcessor");
    }
    if (_.has(processObj, "outputFilename") && !_.isEmpty(processObj, "outputFilename")) {
      //Remove the old output filename
      delete process["outputFilename"];
    }
    process['filenameChanged'] = processObj["filenameChanged"];
    return Object.assign(processObj, _.omit(process, "screenerType"));
  }

  static updateProcess(process) {
    let processKeyMapping = [
      { domainModel: "bluRayProcessor", clientValue: "blu-ray" },
      { domainModel: "dcpProcessor", clientValue: "DCP" },
      { domainModel: "passThroughProcessor", clientValue: "other" },
    ];
    let newObj = Object.assign({}, _.get(process, "screenerEncryption", {}));
    let processObj = Object.assign(newObj, _.omit(process, ["type", "screenerEncryption"]));
    let type = _.get(process, "type");
    if (!_.includes(["transcodingProcessor"], type)) {
      _.set(
        processObj,
        "screenerType",
        _.get(
          _.find(processKeyMapping, (map) => _.get(map, "domainModel") === type),
          "clientValue"
        )
      );
      return processObj;
    }
    return process;
  }

  static getDeliveryFields(destinationsArray) {
    return _.map(destinationsArray, (value) => {
      let deliveryOpt = JobUtils.getDeliveryOptionLabel(_.get(value, "deliveryOption"));
      let destination = Object.assign({}, _.get(deliveryOpt, "destination", ""));
      let keyDestination = _.keys(destination);
      let pickField = _.pick(value, keyDestination);
      let field = Object.assign({}, _.omit(value, keyDestination));
      field["destination"] = Object.assign(destination, pickField);
      field["assetFilename"] = _.get(value, "deliveryFilename", "");
      if (field["driveFormatting"] === "Windows (FAT32)") {
        field["driveFormatting"] = "Windows (NTFS)";
      } 

      field = _.omit(field, [
        "deliveryFilename",
        "deliveryOption"
      ]);
      return field;
    });
  }

  static findMatchingFaspexEntry(account, faspexAccountMapping) {
    return _.find(faspexAccountMapping, (entry) => {
      const matchedByDropbox = _.some(_.get(entry, "destinations", []), (d) => {
        return _.get(d, "attributes.dropboxName") === account;
      });
      if (matchedByDropbox) {
        return true;
      }
      const entryId = _.get(entry, "id");
      return !!entryId && entryId.toUpperCase() === account.toUpperCase();
    });
  }

  
  static attachS3Destinations(deliveryArray, faspexAccountMapping) {
    if (!deliveryArray?.length || !faspexAccountMapping?.length) {
      return deliveryArray;
    }
    return _.map(deliveryArray, (delivery) => {
      const account = _.get(delivery, "destination.account");
      if (!account) {
        return delivery;
      }
      const matchedEntry = JobUtils.findMatchingFaspexEntry(account, faspexAccountMapping);
      if (!matchedEntry) {
        return delivery;
      }
      const otherDestination = _.find(
        _.get(matchedEntry, "destinations", []),
        (d) => _.get(d, "attributes.dropboxName") !== account
      );
      const s3Destination = _.get(otherDestination, "attributes.bucket");
      if (!s3Destination) {
        return delivery;
      }
      return {
        ...delivery,
        destination: {
          ..._.get(delivery, "destination", {}),
          s3Destination
        }
      };
    });
  }

  static isS3OnlyAccount(account, faspexAccountMapping) {
    if (!account || !faspexAccountMapping?.length) {
      return false;
    }
    const matchedEntry = JobUtils.findMatchingFaspexEntry(account, faspexAccountMapping);
    if (!matchedEntry) {
      return false;
    }
    const destinations = _.get(matchedEntry, "destinations", []);
    if (destinations.length !== 1) {
      return false;
    }
    return _.has(destinations[0], "attributes.bucket");
  }

  static filterS3OnlyHeaders(headers, account, faspexAccountMapping) {
    if (!JobUtils.isS3OnlyAccount(account, faspexAccountMapping)) {
      return headers;
    }
    return _.filter(headers, (column) => _.get(column, "fields[0].value") !== "account");
  }

  static getDeliveryOption(configId, dropboxName) {
    const aocMap = AOC_DELIVERY_REGION_MAP[configId];
    if (aocMap) {
      if (dropboxName && aocMap[dropboxName]) {
        return aocMap[dropboxName];
      }
      if (!dropboxName) {
        return aocMap["WBDShare"];
      }
      return configId;
    }

    return DELIVERY_REGION_MAP[configId] || configId;
  }

  static updateDelivery(del) {
    let delObj = Object.assign({}, _.omit(del, "destination"));
    delObj = Object.assign(delObj, _.get(del, "destination"));
    const dropboxname = _.get(del["destination"], "dropboxName", "");
    const locationSubType = _.get(del["destination"], "locationSubType");
    const isShareable = _.get(del["destination"], "shareable");
    delObj["deliveryOption"] = JobUtils.getDeliveryOption(locationSubType, dropboxname);  
    delObj["shareable"] = isShareable || Boolean(dropboxname);
    return _.omit(delObj, ["locationType", "locationSubType"]);
  }

  static getDeliveryRefByLabel(deliveryLabel) {
    return _.get(
      _.find(ConfigUtils.getDeliveryTypes(), (del) => {
        return _.get(del, "label") === deliveryLabel;
      }),
      "ref"
    );
  }

  static getDeliveryRefByDeliveryType(deliveryType, deliveryLabel) {
    return _.get(
      _.find(deliveryType, (del) => {
        return _.get(del, "label") === deliveryLabel;
      }),
      "ref"
    );
  }

  static isQCCheckListEnabled(job, userRole) {
    return userRole !== "requestor" && _.has(job, "processor.transcodeQualityCheck") && _.get(job, "qcRequired", false);
  }

  static isQCPendingAutomated(job) {
    let pendingChange = _.findLast(_.get(job, "auditTrail"), { newValue: "WF_QC_PENDING" });
    return _.get(pendingChange, "createdBy") === "automation";
  }

  static getRequestPayload(
    requestName,
    requestId,
    recordId,
    jobType,
    sourceType,
    isSubmitted,
    notes,
    createdDate,
    createdBy,
    updatedDate,
    updatedBy,
    contacts,
    requestor,
    submittedDate
  ) {
    let requestModel = new RequestModel(
      requestName,
      requestId,
      recordId,
      jobType,
      sourceType,
      isSubmitted,
      notes,
      !_.isEmpty(requestId) ? createdDate : null,
      !_.isEmpty(requestId) ? createdBy : null,
      !_.isEmpty(requestId) ? updatedDate : null,
      !_.isEmpty(requestId) ? updatedBy : null,
      contacts,
      requestor,
      null,
      !_.isEmpty(submittedDate) ? submittedDate : null
    );
    JobUtils.clean(requestModel);
    return requestModel;
  }

  static composeJobs(
    dueDate,
    dueTime,
    requestId,
    jobType,
    status,
    createdDate,
    updatedDate,
    requestor,
    sources,
    processor,
    destinations,
    auditTrail,
    assignedTo,
    sourceType,
    createdBy,
    sourceLocation,
    jobTypeObj,
    contentProtection,
    stitchAssetName,
    isStitch = false,
    stitchedKeys = [],
    faspexAccountMapping = []
  ) {
    try {
      let jobs = [];
      const sourceList = isStitch ? [_.sortBy(sources,"key")[0]] : sources;
      _.map(sourceList, (source) => {
        let mapping = _.get(destinations, "sourceToDeliveryMapping");
        let jobKeysToFilenames = _.get(destinations, "jobKeysToFilename");
        let deliveryFilename = "";
        processor = (processor && processor.length > 0) ? processor : [{}];
        _.map(processor, (process) => {
          let processMapValue = buildKey(source, process);
          deliveryFilename = JobUtils.getDefaultFileName(
            jobTypeObj,
            sourceLocation,
            source,
            _.get(jobKeysToFilenames, processMapValue),
            process,
            stitchAssetName,
            isStitch
          );
          let dest = mapping[processMapValue];
          let destinationArray = [];
          if (!_.isEmpty(dest)) {
            let deliveryOptions = _.get(destinations, "deliveryOptions");
            let arrayDestination = _.map(deliveryOptions, (delivery) => {
              let indexSearch = _.find(dest, (mappedIndex) => mappedIndex === _.get(delivery, "key"));
              if (indexSearch > -1) {
                delivery["deliveryFilename"] = deliveryFilename;
                return Object.assign({}, delivery);
              } else {
                return null;
              }
            });
            destinationArray = _.filter(arrayDestination, (destValue) => {
              return !_.isEmpty(destValue);
            });
          }
          let deliveryArray = JobUtils.getDeliveryFields(destinationArray);
          let filenameChanged = _.get(jobKeysToFilenames, processMapValue) ? true : false;
          let processorModel = new Processors(_.get(deliveryArray[0], "assetFilename", ""), filenameChanged);
          deliveryArray = _.map(deliveryArray, (del) => {
            del = _.omit(_.omit(del, "assetFilename"), "key");
            del = _.omit(_.omit(del, "status"), "statusMessage");
            return del;
          });
          deliveryArray = JobUtils.attachS3Destinations(deliveryArray, faspexAccountMapping);
          let stitchedAssets = null;
          if (stitchedKeys && stitchedKeys.length > 0) {
            stitchedAssets = _.map(stitchedKeys, (key) => sources.find(src => src.key === key));
          } else {
            stitchedAssets = sources;
          }
          let getSource = JobUtils.getSourceFields(source, sourceType, jobType, contentProtection, stitchAssetName, isStitch, stitchedAssets);
          let getProcessor = !_.isEmpty(process) ? JobUtils.getProcessorField(process, processorModel) : processorModel;
          getSource = _.omit(getSource, "key");
          getProcessor = _.omit(getProcessor, "key");

          let job = new Job(
            dueDate,
            dueTime,
            requestId,
            jobType,
            null,
            !_.isEmpty(requestId) ? status : null,
            !_.isEmpty(requestId) ? createdDate : null,
            !_.isEmpty(requestId) ? createdBy : null,
            !_.isEmpty(requestId) ? updatedDate : null,
            null,
            getSource,
            getProcessor,
            deliveryArray,
            auditTrail,
            assignedTo,
            JobUtils.needsSourceOverride(jobType, sourceType),
            JobUtils.needsQC(jobType, sourceType, deliveryArray, requestor)
          );
          JobUtils.clean(job);
          jobs.push(job);
        });
      });
      return jobs;
    } catch (e) {      
      return null;
    }
  }


  static getSelectedProcessingType(job) {
    let selectedJobType = JobUtils.extractJobTypeObjectByValue(_.get(job, "jobType"));
    let refType = "";
    if (_.get(job, "jobType") === "EVENT_SCREENERS") {
      refType = JobUtils.getRefType(_.get(job, "processor.type"))
    }
    return _.get(selectedJobType, "noProcessing") ? {} : JobUtils.getSelectedFileType(_.get(job, "processor.profile.description") || refType);
  }

  static getSelectedProcessingTypeLabel(job) {
    // TO DO - These specific jobTypes checks needs to go away once we decide on making domain
    // model consistent for all jobTypes or we mainintain some other mapping.
    let selectedJobType = JobUtils.extractJobTypeObjectByValue(_.get(job, "jobType"));
    let refType = "";
    if (_.get(job, "jobType") === "EVENT_SCREENERS") {
      refType = JobUtils.getRefType(_.get(job, "processor.type"))
    }
    let selectedFileType =  _.get(selectedJobType, "noProcessing") ? {} : JobUtils.getSelectedFileType(_.get(job, "processor.profile.description") || refType);
    return !_.isEmpty(selectedFileType) ?
        (_.get(job, "jobType") === "EVENT_SCREENERS"
            ? _.get(selectedFileType, "label") : RequestUtils.getFileTypeLabel(_.get(selectedFileType, "ref")))
        : ""
  }

  static getRefType(type) {
    // TO DO - Keeping this mapping for now.
    // Once processing refacotoring is done and ref is correct then no need for it.
    const EVENT_SCREENERS_MAPPING = {
      "bluRayProcessor": "blu-ray",
      "dcpProcessor": "DCP",
      "passThroughProcessor": "other"
    };
    return EVENT_SCREENERS_MAPPING[type];
  }

  static needsSourceOverride(jobType, sourceType) {
    if (sourceType === "SOURCE_OVERRIDE") {
      return true;
    }
    return null;
  }

  // TOOD (bchopra) - We need to look why there is redundant code. We should get rid of it if not required.
  static updateJob(
    requestId,
    jobId,
    requestName,
    dueDate,
    dueTime,
    jobType,
    sourceType,
    notes,
    contacts,
    status,
    sources,
    process,
    destinations,
    auditTrail,
    requestor,
    assignedTo,
    createdDate,
    updatedDate,
    recordId,
    needsSourceOverride,
    createdBy,
    qcRequired,
    sourceLocation,
    jobTypeObj,
    contentProtection,
    stitchAssetName = '',
    isStitch = false,
    stitchedKeys = [],
    faspexAccountMapping = []
  ) {
    try {
      let source = sources[0];
      let mapping = _.get(destinations, "sourceToDeliveryMapping");
      let jobKeysToFilenames = _.get(destinations, "jobKeysToFilename");
      let job = null;
      _.map(_.keys(mapping), (mappingKey) => {
        let deliveryFilename = JobUtils.getDefaultFileName(
          jobTypeObj,
          sourceLocation,
          source,
          _.get(jobKeysToFilenames, mappingKey),
          process,
          stitchAssetName,
          isStitch
        );
        let dest = mapping[mappingKey];
        let destinationArray = _.map(dest, (delKey) => {
          let deliveryOptions = _.get(destinations, "deliveryOptions");
          let del = _.find(deliveryOptions, (option, index) => {
            return _.get(option, "key") === delKey;
          });
          if (del) {
            del["deliveryFilename"] = deliveryFilename;
            return Object.assign({}, del);
          }
        });
        let deliveryArray = JobUtils.getDeliveryFields(destinationArray);
        let filenameChanged = _.get(jobKeysToFilenames, buildKey(source, process))
          || _.get(deliveryArray[0], "assetFilename", "") !== _.get(process, "outputFileName")? true : false;
        let processors = new Processors(_.get(deliveryArray[0], "assetFilename", ""), filenameChanged);
        deliveryArray = _.map(deliveryArray, (del) => {
          del = _.omit(del, ["assetFilename", "key"]);
          return del;
        });
        deliveryArray = JobUtils.attachS3Destinations(deliveryArray, faspexAccountMapping);
        let stitchedAssets = null;
        if (stitchedKeys && stitchedKeys.length > 0) {
          // We may need to think of merging objects also.
          stitchedAssets = _.map(stitchedKeys, (key) => sources.find(src => src.key === key));
        } else {
          stitchedAssets = sources;
        }
        job = new Job(
          dueDate,
          dueTime,
          requestId,
          jobType,
          jobId,
          status,
          createdDate,
          createdBy,
          !_.isEmpty(requestId) ? updatedDate : null,
          !_.isEmpty(requestId) ? requestor.sAMAccountName : null,
          _.omit(JobUtils.getSourceFields(source, sourceType, jobType, contentProtection, stitchAssetName, isStitch, stitchedAssets, sourceLocation), "key"),
          _.omit(JobUtils.getProcessorField(process, processors), "key"),
          deliveryArray,
          auditTrail,
          assignedTo,
          needsSourceOverride,
          qcRequired
        );
        JobUtils.clean(job);
      });
      return job;
    } catch (e) {
      return null;
    }
  }

  static clean(obj) {
    for (let propName in obj) {
      if (obj[propName] === null || obj[propName] === undefined) {
        delete obj[propName];
      }
    }
  }

  static isStitchSource(source) {
    return _.get(source, "type") === "CompositeJobAsset";
  }

  static getSourceType(source) {
    return _.get(source, "sourceLocation.locationSubType");
  }

  static isDrafting(status) {
    return status === Status.DRAFT;
  }

  static validateJob(jobType, sources, processes, deliveries, sourceToDeliveryMapping) {
    let validatedJob = {};
    validatedJob["noSource"] = _.size(sources) === 0;
    let unMappedSources = [];
    if (!_.includes(this.getProcessors(), _.get(jobType, "jobType"))) {
      for (let i = 0; i < _.size(sources); i++) {
        let sourceKey = _.get(sources[i], "key");
        if (!_.has(sourceToDeliveryMapping, sourceKey)) {
          unMappedSources.push(sources[i]);
        }
      }
      validatedJob["hasSPkey"] = false;
    } else {
      _.map(sources, (s) => {
        return _.map(processes, (p) => {
          if (!_.has(sourceToDeliveryMapping, _.get(s, "key") + "_" + _.get(p, "key"))) {
            unMappedSources.push(_.get(s, "key") + "_" + _.get(p, "key"));
          }
        });
      });
      validatedJob["hasSPkey"] = true;
    }
    let mappedDeliveryKeys = new Set();
    _.map(sourceToDeliveryMapping, (deliveryKeyList) => {
      deliveryKeyList.forEach(key => mappedDeliveryKeys.add(key))
    });
    let unmappedDeliveryKeys = new Set();
    _.forEach(deliveries, (delivery) => {
      let key = _.get(delivery, "key");
      if (!mappedDeliveryKeys.has(key)) {
        unmappedDeliveryKeys.add(key);
      }
    });
    validatedJob["unmappedDeliveryKeys"] = unmappedDeliveryKeys;
    validatedJob["unMappedSources"] = unMappedSources;
    return validatedJob;
  }

  static getSelectedFileType(fileType) {

    return _.find(ConfigUtils.getProcessorTypes(), (process) => {
      return _.get(process, "ref") === fileType;
    });
  }


  static showProcessing(jobType, sources) {
    return (
      !_.get(jobType, "noProcessing", false) &&
      !_.get(jobType && jobType.sourceDefinition[0], "processing", false) &&
      _.size(sources) > 0
    );
  }

  /**
   * Detects if a process is an auto-added placeholder (e.g. screenerType: "other" with no user-entered data).
   * Placeholder processes are created when saving a draft with only sources - they bypass the Processing step
   * and incorrectly make the Delivery tab and View Request/Submit button accessible.
   */
  static isPlaceholderProcess(process, jobType) {
    if (!_.includes(JobUtils.getProcessors(), jobType)) {
      return false;
    }
    const hasScreenerTypeOther = _.get(process, "screenerType") === "other";
    const hasProfile = !_.isEmpty(_.get(process, "profile"));
    const hasFileType = !_.isEmpty(_.get(process, "fileType"));
    const hasMeaningfulData = hasProfile || hasFileType || !_.isEmpty(_.get(process, "notes"));
    return hasScreenerTypeOther && !hasMeaningfulData;
  }

  static showDelivery(jobType, processess) {
    return _.includes(this.getProcessors(), _.get(jobType, "jobType")) ? _.size(processess) > 0 : true;
  }

  static extractProcessType(jobType) {
    return _.get(jobType, "processing." + _.get(jobType, "processing.allowedProcesses"));
  }

  /* 
  * The Aspera Share application has been decommissioned.
  * filterRequestResponse method will filter "Aspera Share" from the request deliveries
  * and return the request response in the same format.
  */
  static filterRequestResponse(response){
    const jobs = _.get(response, "jobs");
    const filteredJobs = _.map(jobs, (item) => {
      const filteredDelivery = _.filter(item.delivery, ({destination}) => destination.locationSubType !== "Aspera Share" && destination.locationSubType !== "Screeners.com");
      return {
        ...item,
        delivery: filteredDelivery
      }
    });
    return {...response, jobs: filteredJobs};
  }

  static changeOrder(deliveryObj, keys) {
    const newObj = {};
      _.map(keys, (key) => {
        if (deliveryObj[key]) {
          newObj[key] = deliveryObj[key];
        }
      })
    return newObj;
  }

  static sortDelivery(delivery, keyOrders) {
    const deliveryOptionName = delivery.deliveryOption;
    const metadataKeyOrder = keyOrders[deliveryOptionName]; 
    if (metadataKeyOrder) {
      const deliveryObj = _.omit(delivery, ['key']);
      const deliveryKeyObj = _.pick(delivery, ['key']);
      const newDelivery = JobUtils.changeOrder(deliveryObj, metadataKeyOrder);
      return {...newDelivery, ...deliveryKeyObj };
    }
    return delivery;
  }

  static getProcessKeys(processType, process) {
    if (_.get(processType, 'ref') === 'DCP') {
      if (_.get(process, 'screenerEncryptionType') === 'Theater Projector') {
        return _.get(processType, 'theatreOrderMetadata');
      } else if (_.get(process, 'screenerEncryptionType') === 'Post House') {
        return _.get(processType, 'postHouseOrderMetadata');
      }
    }
    return _.get(processType, 'processOrderMetadata');
  }

  static getSummaryKeys(processor, processType) {
    if (_.has(processor, 'screenerEncryption')) {
      if (_.get(processor, 'screenerEncryption.screenerEncryptionType') === 'Theater Projector') {
        return _.get(processType, 'theatreSummaryMetadata');
      } else if (_.get(processor, 'screenerEncryption.screenerEncryptionType') === 'Post House') {
        return _.get(processType, 'postHouseSummaryMetadata');
      }
    }
    return _.get(processType, 'summaryOrderMetadata');
  }

  static sortProcessingMetadata(process) {
    const selectedProcessType = _.has(process, 'profile') ?
      JobUtils.getSelectedFileType(_.get(process, "profile.description")) : JobUtils.getSelectedFileType(_.get(process, 'screenerType'));
    const keys = JobUtils.getProcessKeys(selectedProcessType, process);
    if (!_.isEmpty(keys)) {
      const newObj = {};
      _.map(keys, (key) => {
        if (process[key]) {
          newObj[key] = process[key];
        }
      })
      return newObj;
    } else {
      return process;
    }
  }

  static orderProcessingSummary(headers, job) {
    const newHeaderArray = [];
    const processor = _.get(job, 'processor');
    const selectedProcessType = JobUtils.getSelectedProcessingType(job);
    const keys = JobUtils.getSummaryKeys(processor, selectedProcessType);
    if (!_.isEmpty(keys)) {
      _.map(keys, (key) => {
        const header = headers.find((header) => header?.fields[0]?.value === key);
        if (header) {
          newHeaderArray.push(header)
        }
      })
      return newHeaderArray;
    } else {
      return headers;
    }
  }
}

